import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close, Delete, Edit, Send } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import axios from '../../../../api/axios';
import { useCRUD } from '../../../../api/crud';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import AttachmentChip from '../../../../components/ui/attachmentChip/attachmentChip.component';
import FileDropZoneField from '../../../../components/ui/fileDropZoneField/fileDropZoneField.component';
import {
  ATTACHMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  MESSAGES_ENDPOINT,
  MESSAGES_MARK_READ_ENDPOINT,
  SUCCESS_SNACKBAR,
} from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { Message, MessageAuthor } from '../../../../types/messages';
import { formatDateTime } from '../../../../utils';
import { JOB_MESSAGES_UPDATED_EVENT } from './viewJob.component';
import styles from './jobMessages.module.css';

const AUTHOR_COLORS = [
  '#1976d2',
  '#388e3c',
  '#d32f2f',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#c2185b',
  '#455a64',
];

type AttachmentChipData = {
  id: number;
  label: string;
  url?: string;
};

const notifyMessagesUpdated = () => {
  window.dispatchEvent(new CustomEvent(JOB_MESSAGES_UPDATED_EVENT));
};

const getAuthorColor = (userId: number): string =>
  AUTHOR_COLORS[Math.abs(userId) % AUTHOR_COLORS.length];

const getAttachmentLabel = (url: string): string => {
  const parts = url.split('/');
  return decodeURIComponent(parts[parts.length - 1] || url);
};

const getAuthorLabel = (author: MessageAuthor): string =>
  author.full_name || author.email;

export const JobMessages = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const { query, create, update, remove, loading } = useCRUD(MESSAGES_ENDPOINT);
  const { remove: removeAttachment } = useCRUD(ATTACHMENTS_ENDPOINT);
  const canViewMessages = useHasPermission('messaging.view_message');
  const canAddMessages = useHasPermission('messaging.add_message');
  const canChangeMessages = useHasPermission('messaging.change_message');
  const canDeleteMessages = useHasPermission('messaging.delete_message');

  const [messages, setMessages] = useState<Message[]>([]);
  const [authorOptions, setAuthorOptions] = useState<MessageAuthor[]>([]);
  const [authorFilter, setAuthorFilter] = useState<number | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [attachmentChips, setAttachmentChips] = useState<AttachmentChipData[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [sending, setSending] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const messagesPath = `/service/jobs/${id}/messages`;
    setOverride(messagesPath, t('Messages'));

    return () => {
      clearOverride(messagesPath);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const markMessagesAsRead = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      await axios.post(MESSAGES_MARK_READ_ENDPOINT, {
        model_name: 'job',
        object_id: id,
      });
      notifyMessagesUpdated();
    } catch {
      // Non-blocking: unread badge will refresh on next fetch.
    }
  }, [id]);

  const loadMessages = useCallback(async () => {
    if (!id || !canViewMessages) {
      return;
    }

    setFetchError(null);
    const response = await query({
      page: 0,
      pageSize: 200,
      sortModel: [{ field: 'created_at', sort: 'asc' }],
      filterModel: {
        quickFilterValues: debouncedSearch ? [debouncedSearch] : [],
        items: [],
      },
      // Scope params, not DataGrid filter items: buildFilterParams drops items
      // without an operator, which would return every message - other users'
      // notifications included.
      extraParams: {
        model_name: 'job',
        object_id: id,
        author: authorFilter !== 'all' ? authorFilter : undefined,
      },
    });

    if (response?.results) {
      setMessages(response.results);
      if (authorFilter === 'all' && !debouncedSearch) {
        const authors = new Map<number, MessageAuthor>();
        response.results.forEach((message) => {
          if (message.author?.id) {
            authors.set(message.author.id, message.author);
          }
        });
        setAuthorOptions(Array.from(authors.values()));
      }
      await markMessagesAsRead();
    } else {
      setFetchError(t('Failed to load messages'));
    }
  }, [authorFilter, canViewMessages, debouncedSearch, id, markMessagesAsRead, query, t]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleUploadAttachment = async (file: File) => {
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('attachment', file);
      const response = await axios.post(ATTACHMENTS_ENDPOINT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const attachment = response.data;
      setAttachmentChips((prev) => [
        ...prev,
        {
          id: attachment.id,
          label: file.name,
          url: attachment.attachment,
        },
      ]);
    } catch {
      enqueueSnackbar(t('Failed to upload attachment'), ERROR_SNACKBAR);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachmentChip = async (chip: AttachmentChipData) => {
    setAttachmentChips((prev) => prev.filter((item) => item.id !== chip.id));
    await removeAttachment({ id: chip.id });
  };

  const handleSendMessage = async () => {
    if (!id || !draftBody.trim()) {
      return;
    }

    setSending(true);
    const { success } = await create({
      body: draftBody.trim(),
      model_name: 'job',
      object_id: id,
      attachment_ids: attachmentChips.map((chip) => chip.id),
    });
    setSending(false);

    if (success) {
      setDraftBody('');
      setAttachmentChips([]);
      enqueueSnackbar(t('Message sent'), SUCCESS_SNACKBAR);
      notifyMessagesUpdated();
      await loadMessages();
      return;
    }

    enqueueSnackbar(t('Failed to send message'), ERROR_SNACKBAR);
  };

  const startEditing = (message: Message) => {
    setEditingMessageId(message.id);
    setEditBody(message.body);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditBody('');
  };

  const handleSaveEdit = async (messageId: number) => {
    if (!editBody.trim()) {
      return;
    }

    setSavingEdit(true);
    const { success } = await update({ id: messageId, body: editBody.trim() });
    setSavingEdit(false);

    if (success) {
      enqueueSnackbar(t('Message updated'), SUCCESS_SNACKBAR);
      cancelEditing();
      notifyMessagesUpdated();
      await loadMessages();
      return;
    }

    enqueueSnackbar(t('Failed to update message'), ERROR_SNACKBAR);
  };

  const handleDeleteMessage = async (messageId: number) => {
    const { success } = await remove({ id: messageId });

    if (success) {
      enqueueSnackbar(t('Message deleted'), SUCCESS_SNACKBAR);
      if (editingMessageId === messageId) {
        cancelEditing();
      }
      notifyMessagesUpdated();
      await loadMessages();
      return;
    }

    enqueueSnackbar(t('Failed to delete message'), ERROR_SNACKBAR);
  };

  if (!canViewMessages) {
    return (
      <Box className={styles.container}>
        <Alert severity="warning">{t('You do not have permission to view messages.')}</Alert>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {t('Messages')}
      </Typography>

      <Box className={styles.splitLayout}>
        <Box className={styles.messagesPane}>
          <Box className={styles.filters}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="message-author-filter">{t('Author')}</InputLabel>
              <Select
                labelId="message-author-filter"
                label={t('Author')}
                value={authorFilter}
                onChange={(event) => setAuthorFilter(event.target.value as number | 'all')}
              >
                <MenuItem value="all">{t('All authors')}</MenuItem>
                {authorOptions.map((author) => (
                  <MenuItem key={author.id} value={author.id}>
                    {getAuthorLabel(author)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label={t('Search messages')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={{ minWidth: 260, flex: 1 }}
            />
          </Box>

          {fetchError && <Alert severity="error">{fetchError}</Alert>}

          <Box className={styles.messagesList}>
            {loading && messages.length === 0 ? (
              <>
                <Skeleton variant="rounded" height={88} />
                <Skeleton variant="rounded" height={88} />
                <Skeleton variant="rounded" height={88} />
              </>
            ) : messages.length === 0 ? (
              <Box className={styles.emptyState}>
                <Typography variant="body1">
                  {t('No messages yet. Write your first message using the form on the right.')}
                </Typography>
              </Box>
            ) : (
              messages.map((message) => {
                const authorColor = getAuthorColor(message.author?.id || 0);
                const isEditing = editingMessageId === message.id;
                const showActions =
                  message.is_own && (canChangeMessages || canDeleteMessages);

                return (
                  <Box
                    key={message.id}
                    className={styles.messageBubble}
                    sx={{
                      backgroundColor: alpha(authorColor, 0.08),
                      borderLeftColor: authorColor,
                      opacity: message.is_read ? 1 : 0.92,
                    }}
                  >
                    <Box className={styles.messageHeader}>
                      <Box className={styles.messageHeaderMain}>
                        <Typography variant="subtitle2" sx={{ color: authorColor, fontWeight: 700 }}>
                          {message.author ? getAuthorLabel(message.author) : t('Deleted user')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(message.created_at)}
                        </Typography>
                        {!message.is_read && !message.is_own && (
                          <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                            {t('Unread')}
                          </Typography>
                        )}
                      </Box>
                      {showActions && !isEditing && (
                        <Box className={styles.messageActions}>
                          {canChangeMessages && (
                            <IconButton
                              size="small"
                              aria-label={t('Edit message')}
                              onClick={() => startEditing(message)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          {canDeleteMessages && (
                            <IconButton
                              size="small"
                              aria-label={t('Delete message')}
                              onClick={() => void handleDeleteMessage(message.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <>
                        <TextField
                          className={styles.messageEditField}
                          multiline
                          minRows={2}
                          fullWidth
                          value={editBody}
                          onChange={(event) => setEditBody(event.target.value)}
                          disabled={savingEdit}
                        />
                        <Box className={styles.messageEditActions}>
                          <Button size="small" onClick={cancelEditing} disabled={savingEdit}>
                            {t('Cancel')}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={savingEdit || !editBody.trim()}
                            onClick={() => void handleSaveEdit(message.id)}
                          >
                            {t('Save')}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" className={styles.messageBody}>
                        {message.body}
                      </Typography>
                    )}

                    {!isEditing && message.attachments?.length > 0 && (
                      <Box className={styles.messageAttachments}>
                        {message.attachments.map((attachment) => (
                          <AttachmentChip
                            key={attachment.id}
                            label={getAttachmentLabel(attachment.attachment)}
                            url={attachment.attachment}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>
        </Box>

        <Box className={styles.composePane}>
          {canAddMessages ? (
            <Box className={styles.composeArea}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('New message')}
              </Typography>

              <TextField
                className={styles.composeTextField}
                multiline
                fullWidth
                label={t('Write a message')}
                value={draftBody}
                onChange={(event) => setDraftBody(event.target.value)}
                disabled={sending}
              />

              {attachmentChips.length > 0 && (
                <Box className={styles.attachmentChips}>
                  {attachmentChips.map((chip) => (
                    <AttachmentChip
                      key={chip.id}
                      label={chip.label}
                      url={chip.url}
                      onDelete={() => void handleRemoveAttachmentChip(chip)}
                      deleteIcon={
                        <IconButton size="small">
                          <Close fontSize="small" />
                        </IconButton>
                      }
                    />
                  ))}
                </Box>
              )}

              <FileDropZoneField
                onFileSelected={handleUploadAttachment}
                uploading={uploadingAttachment}
                disabled={sending}
              />

              <Box className={styles.composeActions}>
                {(sending || loading) && <CircularProgress size={24} />}
                <Button
                  variant="contained"
                  endIcon={<Send />}
                  onClick={() => void handleSendMessage()}
                  disabled={sending || !draftBody.trim()}
                >
                  {t('Send')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className={styles.composePlaceholder}>
              <Typography variant="body2" color="text.secondary">
                {t('You do not have permission to send messages.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
